/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState } from 'react';
import { ToolCall } from '@shared/types/responses/streaming';
import { ChevronDown, ChevronRight, Wrench, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/components/ui/collapsible';

interface ToolCallsListProps {
  toolCalls: ToolCall[];
  isStreaming?: boolean;
  className?: string;
}

export function ToolCallsList({ toolCalls, isStreaming, className }: ToolCallsListProps) {
  const [expandedCalls, setExpandedCalls] = useState<Set<number>>(new Set());

  if (!toolCalls || toolCalls.length === 0) {
    return null;
  }

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedCalls);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCalls(newExpanded);
  };

  const formatToolInput = (input: Record<string, any>) => {
    // Format tool input for display, truncating long values
    const formatted: Record<string, any> = {};
    for (const [key, value] of Object.entries(input)) {
      if (typeof value === 'string' && value.length > 100) {
        formatted[key] = value.substring(0, 100) + '...';
      } else {
        formatted[key] = value;
      }
    }
    return formatted;
  };

  const formatToolOutput = (output: any) => {
    if (!output) return null;
    
    // Handle different output types
    if (typeof output === 'string') {
      return output.length > 200 ? output.substring(0, 200) + '...' : output;
    }
    
    if (typeof output === 'object') {
      try {
        const jsonStr = JSON.stringify(output, null, 2);
        return jsonStr.length > 300 ? jsonStr.substring(0, 300) + '...' : jsonStr;
      } catch {
        return '[Complex Object]';
      }
    }
    
    return String(output);
  };

  return (
    <div className={cn("w-full mt-3 space-y-2", className)}>
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Wrench className="h-3 w-3" />
        Tool Calls ({toolCalls.length})
      </div>
      
      {toolCalls.map((toolCall, index) => {
        const isExpanded = expandedCalls.has(index);
        // Use tool call ID if available, otherwise fall back to index
        // This prevents duplicate key warnings when multiple messages have tool calls
        const key = toolCall.id || `tool-call-${index}`;
        
        return (
          <Collapsible key={key} open={isExpanded} onOpenChange={() => toggleExpanded(index)}>
            <CollapsibleTrigger asChild>
              <div className={cn(
                "flex items-center justify-between p-2 rounded-md border cursor-pointer transition-colors",
                "border-border/50 bg-muted/30 hover:bg-muted/50",
                toolCall.success === undefined && isStreaming && "border-blue-300 bg-blue-50 animate-pulse"
              )}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {toolCall.success === undefined ? (
                    <Loader2 className="h-3 w-3 text-blue-600 flex-shrink-0 animate-spin" />
                  ) : toolCall.success ? (
                    <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                  )}
                  <span className="text-xs font-medium truncate">{toolCall.toolName}</span>
                  {toolCall.executionTime && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {toolCall.executionTime}ms
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-1">
              <div className="p-3 rounded-md bg-muted/20 border border-border/30 space-y-3">
                {/* Tool Input */}
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Input:</div>
                  <pre className="text-xs bg-background rounded p-2 overflow-x-auto border">
                    {JSON.stringify(formatToolInput(toolCall.input), null, 2)}
                  </pre>
                </div>
                
                {/* Tool Output */}
                {toolCall.output && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">Output:</div>
                    <pre className="text-xs bg-background rounded p-2 overflow-x-auto border whitespace-pre-wrap">
                      {formatToolOutput(toolCall.output)}
                    </pre>
                  </div>
                )}
                
                {/* Error */}
                {toolCall.error && (
                  <div>
                    <div className="text-xs font-medium text-red-600 mb-1">Error:</div>
                    <div className="text-xs text-red-700 bg-red-50 rounded p-2 border border-red-200">
                      {toolCall.error}
                    </div>
                  </div>
                )}
                
                {/* Execution Time */}
                {toolCall.executionTime && (
                  <div className="text-xs text-muted-foreground">
                    Execution time: {toolCall.executionTime}ms
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
